import { ReactNode, useEffect, useState } from 'react';
import { P2GDropdown, P2GGrid, P2GRadio, P2GTextField } from '@parcel2go/core-ui';
import { Country, Customer, InputRuleApiModel } from '~/lib/api/smartsend/types';
import { GenerateNumberOptions } from '../../lib/helpers/ArrayHelper';
import { toDisplayCurrency } from '~/components/currency/toDisplayCurrency';
import { CountryOfManufactureField } from '../customs/CountryOfManufactureField';
import { TariffCodeField } from '../customs/TariffCodeField';
import conditionalIsRequired from '../../lib/helpers/CustomerConditionalHelper';
import { TranslationNamespaces } from '~/lib/constants';
import { useTranslation } from 'react-i18next';

interface props {
  description: string;
  descriptionLabel?: string;
  onDescriptionChange?: (value: string) => void;

  sku: string;
  onSkuChange?: (value: string) => void;
  skuMode: 'editable' | 'readonly';

  quantity: number;
  onQuantityChange?: (value: number) => void;
  quantityMode: 'dropdown' | 'text' | 'readonly';

  value: number;
  onValueChange?: (value: number) => void;
  currencyCode: string;
  valueAdornment?: ReactNode;
  valueMode: 'editable' | 'readonly';

  showUnitTotalToggle?: boolean;
  unit?: 'UNIT' | 'TOTAL';
  onUnitChange?: (unit: 'UNIT' | 'TOTAL') => void;

  countryOfManufacture: string;
  onCountryOfManufactureChange?: (value: string) => void;
  countries?: Country[];

  tariffCode: string;
  onTariffCodeChange?: (value: string) => void;
  tariffSystem?: string;

  productValidation?: InputRuleApiModel[];
  sender?: Customer;
  recipient?: Customer;
}

export const ProductContentFields: React.FC<props> = (props) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  // Matched on name alone, not `&& x.required`: the field is offered whenever the service has a rule
  // for it, so a user can fill it in even when it is optional - only its required-ness varies. Carried
  // over from the inline version in ContentsSelectionRow that this component replaced.
  const countryRequirement = props.productValidation?.find((x) => x.name === 'CountryOfManufacture');
  const tariffCodeRequirement = props.productValidation?.find((x) => x.name === 'TariffCode');

  // Holds the in-progress text so typing a decimal point isn't immediately round-tripped through
  // Number() and snapped back (e.g. "12." -> Number -> 12 -> "12", losing the "." the user just
  // typed). Only resyncs from props when the numeric value changes for a reason other than this
  // field's own edits (e.g. currency conversion elsewhere), not on every keystroke's own echo.
  const [valueText, setValueText] = useState(() => (props.value == null ? '' : String(props.value)));

  useEffect(() => {
    if (Number(valueText) !== props.value) {
      setValueText(props.value == null ? '' : String(props.value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.value]);

  const onValueInputChange = (raw: string) => {
    setValueText(raw);
    // Don't propagate incomplete/empty input - it would round-trip back through props.value and
    // clobber what the user is still typing (a trailing "." or "-", or nothing at all).
    if (raw === '' || /[.-]$/.test(raw) || Number.isNaN(Number(raw))) {
      return;
    }
    props.onValueChange?.(Number(raw));
  };

  const onValueInputBlur = () => {
    const parsed = Number(valueText);
    if (valueText === '' || Number.isNaN(parsed)) {
      setValueText('0');
      props.onValueChange?.(0);
      return;
    }
    setValueText(String(parsed));
    props.onValueChange?.(parsed);
  };

  return (
    <>
      <P2GGrid container gap={2} alignItems="center" wrap="nowrap" mb={2}>
        <P2GGrid item flexGrow={1}>
          <P2GTextField
            fullWidth
            showErrorsWhenPristine={true}
            required
            disabled={!props.onDescriptionChange}
            value={props.description || ''}
            onChange={(v) => props.onDescriptionChange?.(v.currentTarget.value)}
            label={props.descriptionLabel || t('ITEM_DESCRIPTION_INPUT_LABEL', { defaultValue: 'Item Description' })}
            id="Description"
          />
        </P2GGrid>
      </P2GGrid>
      <P2GGrid container spacing={2} alignItems="center">
        <P2GGrid item xs={6} sm={3}>
          {props.quantityMode === 'readonly' ? (
            <P2GTextField
              fullWidth
              disabled
              label={t('QUANTITY_INPUT_LABEL', { defaultValue: 'Quantity' })}
              id="Quantity"
              value={String(props.quantity)}
              onChange={() => {}}
            />
          ) : props.quantityMode === 'dropdown' ? (
            <P2GDropdown
              fullWidth
              value={props.quantity as any}
              options={GenerateNumberOptions(1, 100)}
              label={t('QUANTITY_INPUT_LABEL', { defaultValue: 'Quantity' })}
              id="Quantity"
              onChange={(v) => props.onQuantityChange?.(Number(v))}
            />
          ) : (
            <P2GTextField
              fullWidth
              type="number"
              label={t('QUANTITY_INPUT_LABEL', { defaultValue: 'Quantity' })}
              id="Quantity"
              value={String(props.quantity)}
              onChange={(v) => props.onQuantityChange?.(Number(v.currentTarget.value) || 0)}
            />
          )}
        </P2GGrid>
        <P2GGrid item xs={6} sm={4}>
          {props.valueMode === 'readonly' ? (
            <P2GTextField
              fullWidth
              disabled
              label={t('VALUE_INPUT_LABEL', { defaultValue: 'Value' })}
              id="Value"
              value={toDisplayCurrency(props.currencyCode, props.value)}
              onChange={() => {}}
            />
          ) : (
            <P2GTextField
              fullWidth
              startAdornment={props.valueAdornment}
              value={valueText}
              required
              min={0.01}
              onChange={(v) => onValueInputChange(v.currentTarget.value)}
              onBlur={onValueInputBlur}
              label={t('VALUE_INPUT_LABEL', { defaultValue: 'Value' })}
              showErrorsWhenPristine={true}
              id="Value"
            />
          )}
        </P2GGrid>
        <P2GGrid item xs={12} sm={5}>
          {props.skuMode === 'readonly' ? (
            <P2GTextField
              fullWidth
              disabled
              label={t('SKU_INPUT_LABEL', { defaultValue: 'SKU' })}
              id="SKU"
              value={props.sku || ''}
              onChange={() => {}}
            />
          ) : (
            <P2GTextField
              fullWidth
              showErrorsWhenPristine={true}
              disabled={!props.onSkuChange}
              value={props.sku || ''}
              onChange={(v) => props.onSkuChange?.(v.currentTarget.value)}
              label={t('SKU_INPUT_LABEL', { defaultValue: 'SKU' })}
              id="SKU"
            />
          )}
        </P2GGrid>
        {props.showUnitTotalToggle && props.quantity > 1 && (
          <P2GGrid item xs={12}>
            <P2GRadio
              value="UNIT"
              label={t('UNIT_COST_RADIO_BUTTON', { defaultValue: 'Unit Cost' })}
              onChange={() => props.onUnitChange?.('UNIT')}
              name="Unit"
              id="Unit"
              checked={props.unit === 'UNIT'}
            />
            <P2GRadio
              value="TOTAL"
              label={t('TOTAL_COST_RADIO_BUTTON', { defaultValue: 'Total Cost' })}
              onChange={() => props.onUnitChange?.('TOTAL')}
              name="Unit"
              id="Total"
              checked={props.unit === 'TOTAL'}
            />
          </P2GGrid>
        )}
      </P2GGrid>
      {(countryRequirement || tariffCodeRequirement) && (
        <P2GGrid container gap={2} mt={2} alignItems="center">
          {countryRequirement && (
            <CountryOfManufactureField
              value={props.countryOfManufacture}
              onChange={(v) => props.onCountryOfManufactureChange?.(v)}
              countries={props.countries || []}
              disabled={!props.onCountryOfManufactureChange}
              required={
                conditionalIsRequired(countryRequirement, 'Sender', props.sender, props.recipient) ??
                countryRequirement?.required
              }
            />
          )}
          {tariffCodeRequirement && (
            <TariffCodeField
              value={props.tariffCode}
              onChange={(v) => props.onTariffCodeChange?.(v)}
              disabled={!props.onTariffCodeChange}
              required={
                conditionalIsRequired(tariffCodeRequirement, 'Sender', props.sender, props.recipient) ??
                tariffCodeRequirement?.required
              }
              system={props.tariffSystem}
              searchTerm={props.description}
            />
          )}
        </P2GGrid>
      )}
    </>
  );
};
